/**
 * Builds Apache ECharts option objects for every supported analyst chart type.
 * Reads the canonical data shapes defined in `agentic-analytics-api-contract.md`
 * (§5): SERIES (cartesian), CATEGORICAL (name/value), HIERARCHY (tree nodes),
 * and XY (points / matrix). Kept framework-agnostic (returns plain option
 * objects) so it can be unit tested without a DOM.
 */
import {
  AnalystChartConfig,
  AnalystSeries,
  asString,
  CHART_TOKENS,
  formatValue,
  resolveColor,
  resolvePalette,
} from './agenticTypes';

type EChartsOption = Record<string, any>;

const GRID_COLOR = CHART_TOKENS['chart-grid'];
const AXIS_COLOR = '#94A3B8';
const TITLE_COLOR = CHART_TOKENS['chart-accent'];

// Content1 surface tokens (light values); AnalystChart injects the dark ones.
const CONTENT1_BG = '#ffffff';
const CONTENT1_FG = '#11181C';
const CONTENT1_BORDER = '#E2E8F0';
const FONT = {
  title: { fontFamily: 'Oswald, sans-serif', fontSize: 16, fontWeight: 600, color: TITLE_COLOR },
  axis: { fontFamily: 'Inter, sans-serif', fontSize: 11, color: AXIS_COLOR },
};

function titleBlock(text?: string): EChartsOption | undefined {
  if (!text) return undefined;
  return { title: { text, left: 'center', top: 2, textStyle: FONT.title } };
}

/**
 * Computes grid margins, reserving a top "band" for whatever sits above the
 * plot — the legend (top-right) and/or the value-axis name (top-left). Without
 * enough headroom ECharts clips the axis name / topmost tick (e.g. stacked bar).
 * NOTE: `containLabel` accounts for tick labels but NOT axis *names*, so the
 * top/bottom margins must explicitly reserve room for any axis title.
 */
function baseGrid(hasTitle: boolean, hasLegend = false, hasYName = false) {
  let top = 12;
  if (hasTitle) top += 24;
  if (hasLegend || hasYName) top += 22;
  return { left: 56, right: 24, top, bottom: 52, containLabel: true };
}

/**
 * Legend pinned to the top-right so it never overlaps the value-axis name
 * (rendered top-left). Scroll mode keeps many series from spilling over.
 */
function topLegend(hasTitle: boolean): EChartsOption {
  return { type: 'scroll', top: hasTitle ? 28 : 6, right: 8, left: 'auto', textStyle: FONT.axis, itemWidth: 14, itemHeight: 10 };
}

/** Shared style for a top-left value-axis name so it stays in the reserved band. */
const Y_NAME_STYLE = { ...FONT.axis, align: 'left' as const };

/** Rotation tier for crowded category axes so every label can still render. */
function categoryRotate(count: number): number {
  if (count > 14) return 45;
  if (count > 6) return 30;
  return 0;
}

/**
 * Category (x) axis with a bottom-centred title and *all* labels shown
 * (`interval: 0`). Rotation scales with the category count to avoid clipping.
 */
function categoryXAxis(config: AnalystChartConfig, cats: string[]): EChartsOption {
  const rotate = categoryRotate(cats.length);
  return {
    type: 'category',
    data: cats,
    name: config.xLabel,
    nameLocation: 'middle',
    // Gap must clear the (possibly rotated) tick labels so the title sits below them.
    nameGap: rotate ? 56 : 28,
    nameTextStyle: { ...FONT.axis, fontWeight: 600 },
    axisLabel: { ...FONT.axis, interval: 0, rotate },
    axisLine: { lineStyle: { color: GRID_COLOR } },
  };
}

/** Value (y) axis with a non-clipping top-left title. */
function valueYAxis(config: AnalystChartConfig, extra: EChartsOption = {}): EChartsOption {
  return {
    type: 'value',
    name: config.yLabel,
    nameLocation: 'end',
    nameGap: 12,
    nameTextStyle: Y_NAME_STYLE,
    axisLabel: { ...FONT.axis, formatter: axisFmt },
    splitLine: { lineStyle: { color: GRID_COLOR } },
    ...extra,
  };
}

/**
 * Grid for a vertical cartesian chart. Reserves bottom room for rotated tick
 * labels AND the x-axis title (which `containLabel` ignores).
 */
function cartesianGrid(
  config: AnalystChartConfig,
  cats: string[],
  opts: { hasTitle: boolean; hasLegend?: boolean; right?: number }
): EChartsOption {
  const rotate = categoryRotate(cats.length);
  const base = baseGrid(opts.hasTitle, opts.hasLegend, Boolean(config.yLabel));
  let bottom = 30;
  if (rotate) bottom += 34;
  if (config.xLabel) bottom += 22;
  return { ...base, right: opts.right ?? base.right, bottom };
}

/** Compact number formatting for axis labels / value labels. */
const axisFmt = (value: number) => formatValue(value, 'number-compact');
const valueFmt = (value: number) => formatValue(value, 'number');

function rowsOf(config: AnalystChartConfig): Record<string, unknown>[] {
  return Array.isArray(config.data) ? config.data : [];
}

/** Resolve the X / category column, inferring from the first string column. */
function inferXField(config: AnalystChartConfig): string {
  if (config.xField) return config.xField;
  if (config.categoryField) return config.categoryField;
  const first = rowsOf(config)[0];
  if (first) {
    const stringKey = Object.keys(first).find((key) => typeof first[key] === 'string');
    return stringKey ?? Object.keys(first)[0] ?? '';
  }
  return '';
}

/** Resolve the value series, inferring from numeric columns when omitted. */
function inferSeries(config: AnalystChartConfig): AnalystSeries[] {
  if (Array.isArray(config.yFields) && config.yFields.length > 0) return config.yFields;
  if (config.yField) return [{ field: config.yField, label: config.yField }];
  const xField = inferXField(config);
  const first = rowsOf(config)[0];
  if (!first) return [];
  return Object.keys(first)
    .filter((key) => key !== xField && typeof first[key] === 'number')
    .map((key) => ({ field: key, label: key }));
}

/** CATEGORICAL rows → `{ name, value, color }` with the documented aliases. */
function categoricalData(config: AnalystChartConfig): { name: string; value: number; color?: string }[] {
  return rowsOf(config).map((row) => {
    const color = row['color'];
    return {
      name: asString(row['name'] ?? row['label'] ?? row['category']),
      value: Number(row['value'] ?? row['count'] ?? row['amount'] ?? 0),
      color: typeof color === 'string' ? resolveColor(color) : undefined,
    };
  });
}

/* ---------------------------------- Cartesian ---------------------------------- */

/**
 * Pivot long-form rows (e.g. SQL `GROUP BY x, seriesField`) into wide-form
 * ECharts series — one series per distinct value of `seriesField`.
 */
function buildPivotedCartesian(config: AnalystChartConfig): EChartsOption {
  const hasTitle = Boolean(config.title);
  const chartType = config.chartType;
  const xField = inferXField(config);
  const seriesField = config.seriesField ?? '';
  const baseSeries = inferSeries(config)[0];
  const valueField = baseSeries?.field;
  const isBarFamily = chartType === 'bar' || chartType === 'multi_bar' || chartType === 'stacked_bar';
  const stackAll = chartType === 'stacked_bar';
  const palette = resolvePalette(config.colors);
  const rows = rowsOf(config);
  const xCats = Array.from(new Set(rows.map((row) => asString(row[xField]))));
  const seriesValues = Array.from(new Set(rows.map((row) => asString(row[seriesField]))));
  const lookup = new Map<string, number>();
  rows.forEach((row) => {
    const key = `${asString(row[xField])}__${asString(row[seriesField])}`;
    lookup.set(key, Number(row[valueField ?? ''] ?? 0));
  });
  const showLegend = seriesValues.length > 1;

  return {
    ...titleBlock(config.title),
    tooltip: { trigger: 'axis', axisPointer: { type: isBarFamily ? 'shadow' : 'line' } },
    legend: showLegend ? topLegend(hasTitle) : undefined,
    grid: cartesianGrid(config, xCats, { hasTitle, hasLegend: showLegend }),
    xAxis: categoryXAxis(config, xCats),
    yAxis: valueYAxis(config),
    series: seriesValues.map((seriesValue, index) => {
      const color = palette[index % palette.length];
      const singleLine = !isBarFamily && seriesValues.length === 1;
      return {
        name: seriesValue,
        type: isBarFamily ? 'bar' : 'line',
        stack: stackAll ? 'total' : baseSeries?.stack,
        smooth: !isBarFamily,
        showSymbol: !isBarFamily && xCats.length <= 12,
        barMaxWidth: 36,
        // Rounded tops on standalone bars; skipped when stacked so segments stay flush.
        itemStyle: { color, borderRadius: isBarFamily && !stackAll ? [3, 3, 0, 0] : undefined },
        lineStyle: isBarFamily ? undefined : { color, width: 2 },
        areaStyle: singleLine ? { color, opacity: 0.1 } : undefined,
        data: xCats.map((xCat) => lookup.get(`${xCat}__${seriesValue}`) ?? 0),
      };
    }),
  };
}

function buildCartesian(config: AnalystChartConfig): EChartsOption {
  if (config.seriesField) return buildPivotedCartesian(config);
  const hasTitle = Boolean(config.title);
  const chartType = config.chartType;
  const xField = inferXField(config);
  const series = inferSeries(config);
  const isBarFamily = chartType === 'bar' || chartType === 'multi_bar' || chartType === 'stacked_bar';
  const isStep = chartType === 'step';
  const stackAll = chartType === 'stacked_bar';
  const cats = rowsOf(config).map((row) => asString(row[xField]));
  const palette = resolvePalette(config.colors);
  const showLegend = series.length > 1;

  return {
    ...titleBlock(config.title),
    tooltip: { trigger: 'axis', axisPointer: { type: isBarFamily ? 'shadow' : 'line' } },
    legend: showLegend ? topLegend(hasTitle) : undefined,
    grid: cartesianGrid(config, cats, { hasTitle, hasLegend: showLegend }),
    xAxis: categoryXAxis(config, cats),
    yAxis: valueYAxis(config),
    series: series.map((serie, index) => {
      const color = resolveColor(serie.color, palette[index % palette.length]);
      const singleLine = !isBarFamily && series.length === 1;
      return {
        name: serie.label ?? serie.field,
        type: isBarFamily ? 'bar' : 'line',
        stack: stackAll ? 'total' : serie.stack,
        // Stepped lines are not smoothed; ECharts renders right-angle steps instead.
        smooth: !isBarFamily && !isStep,
        step: isStep ? 'end' : undefined,
        showSymbol: !isBarFamily && cats.length <= 12,
        barMaxWidth: 36,
        // Rounded tops on standalone bars; skipped when stacked so segments stay flush.
        itemStyle: { color, borderRadius: isBarFamily && !stackAll ? [3, 3, 0, 0] : undefined },
        lineStyle: isBarFamily ? undefined : { color, width: 2 },
        areaStyle: singleLine ? { color, opacity: 0.1 } : undefined,
        data: rowsOf(config).map((row) => Number(row[serie.field] ?? 0)),
      };
    }),
  };
}

function buildHbar(config: AnalystChartConfig): EChartsOption {
  const hasTitle = Boolean(config.title);
  const xField = inferXField(config);
  const serie = inferSeries(config)[0];
  const palette = resolvePalette(config.colors);
  const cats = rowsOf(config).map((row) => asString(row[xField]));
  return {
    ...titleBlock(config.title),
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    // Extra left padding so long category names are not clipped; bottom leaves
    // room for the value-axis tick labels AND its axis name beneath them; top
    // leaves room for the category-axis name ("User") that floats above the plot.
    grid: {
      ...baseGrid(hasTitle),
      left: 12,
      right: 64,
      top: config.xLabel ? Math.max(baseGrid(hasTitle).top, 30) : baseGrid(hasTitle).top,
      bottom: config.yLabel ? 64 : 40,
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      name: config.yLabel,
      nameLocation: 'middle',
      // Gap must clear the tick labels (≈22px) so the name doesn't overlap them.
      nameGap: 40,
      nameTextStyle: { ...FONT.axis, fontWeight: 600 },
      axisLabel: { ...FONT.axis, formatter: axisFmt },
      splitLine: { lineStyle: { color: GRID_COLOR } },
    },
    yAxis: {
      type: 'category',
      inverse: true,
      data: cats,
      name: config.xLabel,
      // With inverse:true the axis "start" is the top, so the category-axis name
      // sits at the top-left instead of colliding with the value-axis "0" tick.
      nameLocation: 'start',
      nameGap: 10,
      nameTextStyle: { ...FONT.axis, align: 'left', fontWeight: 600 },
      // interval: 0 forces every category label to render (ECharts otherwise auto-skips).
      axisLabel: { ...FONT.axis, interval: 0 },
      axisLine: { lineStyle: { color: GRID_COLOR } },
    },
    series: [
      {
        type: 'bar',
        barMaxWidth: 22,
        itemStyle: { color: resolveColor(serie?.color, palette[0]), borderRadius: [0, 3, 3, 0] },
        label: { show: true, position: 'right', ...FONT.axis, formatter: (p: any) => valueFmt(p.value) },
        data: rowsOf(config).map((row) => Number(row[serie?.field ?? ''] ?? 0)),
      },
    ],
  };
}

/* ---------------------------------- Donut ---------------------------------- */

function buildDonut(config: AnalystChartConfig): EChartsOption {
  const data = categoricalData(config).map((slice) => ({
    name: slice.name,
    value: slice.value,
    itemStyle: slice.color ? { color: slice.color } : undefined,
  }));
  return {
    ...titleBlock(config.title),
    tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
    legend: { orient: 'vertical', right: 12, top: 'middle', textStyle: FONT.axis, itemWidth: 10, itemHeight: 10 },
    color: resolvePalette(config.colors),
    series: [
      {
        type: 'pie',
        radius: ['42%', '68%'],
        center: ['38%', '52%'],
        avoidLabelOverlap: true,
        label: { show: true, formatter: '{d}%', ...FONT.axis },
        labelLine: { length: 6, length2: 6 },
        data,
      },
    ],
  };
}

/* ---------------------------------- Funnel ---------------------------------- */

function buildFunnel(config: AnalystChartConfig): EChartsOption {
  const palette = resolvePalette(config.colors);
  const data = categoricalData(config);
  return {
    ...titleBlock(config.title),
    tooltip: { trigger: 'item', formatter: (p: any) => `${p.name}: ${valueFmt(p.value)}` },
    series: [
      {
        type: 'funnel',
        top: config.title ? 44 : 12,
        bottom: 12,
        left: '8%',
        right: '8%',
        sort: 'descending',
        gap: 2,
        minSize: '24%',
        label: { show: true, position: 'inside', formatter: (p: any) => `${p.name}  ${valueFmt(p.value)}`, ...FONT.axis, color: '#fff', fontSize: 12 },
        data: data.map((stage, index) => ({
          name: stage.name,
          value: stage.value,
          itemStyle: { color: stage.color ?? palette[index % palette.length] },
        })),
      },
    ],
  };
}

/* ---------------------------------- Gauge ---------------------------------- */

function buildGauge(config: AnalystChartConfig): EChartsOption {
  const data = categoricalData(config);
  const value = typeof config.value === 'number' ? config.value : data[0]?.value ?? 0;
  const palette = resolvePalette(config.colors);
  const color = data[0]?.color ?? palette[0];
  return {
    ...titleBlock(config.title),
    series: [
      {
        type: 'gauge',
        min: config.min ?? 0,
        max: config.max ?? 100,
        center: ['50%', '62%'],
        radius: '78%',
        progress: { show: true, width: 14, itemStyle: { color } },
        axisLine: { lineStyle: { width: 14, color: [[1, GRID_COLOR]] } },
        axisTick: { show: false },
        splitLine: { length: 10, lineStyle: { color: AXIS_COLOR } },
        axisLabel: { ...FONT.axis, distance: 18 },
        pointer: { itemStyle: { color } },
        anchor: { show: true, size: 14, itemStyle: { color } },
        title: { show: false },
        detail: {
          valueAnimation: true,
          formatter: () => valueFmt(value),
          color: TITLE_COLOR,
          fontFamily: 'Oswald, sans-serif',
          fontSize: 26,
          fontWeight: 700,
          offsetCenter: [0, '40%'],
        },
        data: [{ value }],
      },
    ],
  };
}

/* ---------------------------------- Pareto ---------------------------------- */

function buildPareto(config: AnalystChartConfig): EChartsOption {
  const hasTitle = Boolean(config.title);
  const xField = inferXField(config);
  const serie = inferSeries(config)[0];
  const ranked = rowsOf(config)
    .map((row) => ({ name: asString(row[xField]), value: Number(row[serie?.field ?? ''] ?? 0) }))
    .sort((a, b) => b.value - a.value);
  const total = ranked.reduce((sum, item) => sum + item.value, 0) || 1;
  let running = 0;
  const cumulative = ranked.map((item) => {
    running += item.value;
    return Number(((running / total) * 100).toFixed(1));
  });
  const paretoCats = ranked.map((item) => item.name);
  return {
    ...titleBlock(config.title),
    tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
    legend: topLegend(hasTitle),
    grid: cartesianGrid(config, paretoCats, { hasTitle, hasLegend: true, right: 56 }),
    xAxis: categoryXAxis(config, paretoCats),
    yAxis: [
      valueYAxis(config),
      { type: 'value', name: '%', nameTextStyle: FONT.axis, min: 0, max: 100, axisLabel: { ...FONT.axis, formatter: '{value}%' }, splitLine: { show: false } },
    ],
    series: [
      {
        name: serie?.label ?? config.yLabel ?? 'Value',
        type: 'bar',
        barMaxWidth: 36,
        itemStyle: { color: resolveColor(serie?.color) },
        data: ranked.map((item) => item.value),
      },
      {
        name: 'Cumulative %',
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        symbolSize: 6,
        itemStyle: { color: resolveColor(config.colors?.[1]) },
        data: cumulative,
        // The classic Pareto "80% / vital few" reference line.
        markLine: {
          silent: true,
          symbol: 'none',
          label: { formatter: '80%', position: 'insideEndTop', ...FONT.axis, color: CHART_TOKENS['chart-amber'] },
          lineStyle: { color: CHART_TOKENS['chart-amber'], type: 'dashed', width: 1.5 },
          data: [{ yAxis: 80 }],
        },
      },
    ],
  };
}

/* ---------------------------------- Waterfall ---------------------------------- */

function buildWaterfall(config: AnalystChartConfig): EChartsOption {
  const hasTitle = Boolean(config.title);
  const xField = inferXField(config);
  const serie = inferSeries(config)[0];
  const field = serie?.field ?? 'delta';
  const seriesColor = resolveColor(serie?.color);
  const rows = rowsOf(config);
  const labels = rows.map((row) => asString(row[xField]));
  const values = rows.map((row) => Number(row[field] ?? 0));

  const placeholders: number[] = [];
  const increases: (number | string)[] = [];
  const decreases: (number | string)[] = [];
  const totals: (number | string)[] = [];

  let running = 0;
  values.forEach((value, index) => {
    const isTotal = index === 0 || index === values.length - 1;
    if (isTotal) {
      placeholders.push(0);
      increases.push('-');
      decreases.push('-');
      totals.push(value);
      running = value;
    } else if (value >= 0) {
      placeholders.push(running);
      increases.push(value);
      decreases.push('-');
      totals.push('-');
      running += value;
    } else {
      running += value;
      placeholders.push(running);
      increases.push('-');
      decreases.push(-value);
      totals.push('-');
    }
  });

  const palette = resolvePalette(config.colors);
  const increaseColor = palette[0] ?? seriesColor;
  const decreaseColor = palette[1] ?? resolveColor('chart-highlight');
  const totalColor = palette[2] ?? seriesColor;

  return {
    ...titleBlock(config.title),
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: cartesianGrid(config, labels, { hasTitle, hasLegend: false }),
    xAxis: categoryXAxis(config, labels),
    yAxis: valueYAxis(config),
    series: [
      { type: 'bar', stack: 'all', itemStyle: { borderColor: 'transparent', color: 'transparent' }, emphasis: { itemStyle: { color: 'transparent' } }, data: placeholders },
      { name: 'Increase', type: 'bar', stack: 'all', itemStyle: { color: increaseColor }, data: increases },
      { name: 'Decrease', type: 'bar', stack: 'all', itemStyle: { color: decreaseColor }, data: decreases },
      { name: 'Total', type: 'bar', stack: 'all', itemStyle: { color: totalColor }, data: totals },
    ],
  };
}

/* ---------------------------------- Bullet ---------------------------------- */

function buildBullet(config: AnalystChartConfig): EChartsOption {
  const hasTitle = Boolean(config.title);
  const xField = inferXField(config);
  const actualField = inferSeries(config)[0]?.field ?? 'actual';
  const rows = rowsOf(config);
  const labels = rows.map((row) => asString(row[xField]));
  const values = rows.map((row) => Number(row[actualField] ?? 0));
  const targetOf = (row: Record<string, unknown>): number | undefined => {
    if (config.targetField && typeof row[config.targetField] === 'number') return row[config.targetField] as number;
    if (typeof config.target === 'number') return config.target;
    return undefined;
  };
  const targets = rows.map(targetOf);
  const numericTargets = targets.filter((target): target is number => typeof target === 'number');
  const maxValue = Math.max(1, ...values, ...numericTargets);

  return {
    ...titleBlock(config.title),
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    // Horizontal layout: value-axis title goes bottom-centre, category-axis title top-left (like hbar).
    grid: {
      ...baseGrid(hasTitle),
      left: 12,
      right: 56,
      top: config.xLabel ? Math.max(baseGrid(hasTitle).top, 30) : baseGrid(hasTitle).top,
      bottom: config.yLabel ? 60 : 40,
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      max: maxValue,
      name: config.yLabel,
      nameLocation: 'middle',
      nameGap: 36,
      nameTextStyle: { ...FONT.axis, fontWeight: 600 },
      axisLabel: { ...FONT.axis, formatter: axisFmt },
      splitLine: { lineStyle: { color: GRID_COLOR } },
    },
    yAxis: {
      type: 'category',
      inverse: true,
      data: labels,
      name: config.xLabel,
      nameLocation: 'start',
      nameGap: 10,
      nameTextStyle: { ...FONT.axis, align: 'left', fontWeight: 600 },
      axisLabel: { ...FONT.axis, interval: 0 },
      axisLine: { lineStyle: { color: GRID_COLOR } },
    },
    series: [
      {
        type: 'bar',
        barWidth: 14,
        itemStyle: {
          borderRadius: [0, 3, 3, 0],
          color: resolveColor(inferSeries(config)[0]?.color),
        },
        label: { show: true, position: 'right', ...FONT.axis, formatter: (p: any) => valueFmt(p.value) },
        data: values,
        markLine: {
          symbol: 'none',
          label: { show: false },
          lineStyle: { color: resolveColor(config.colors?.[1]), type: 'solid', width: 2 },
          data: targets
            .map((target) => (typeof target === 'number' ? { xAxis: target, yAxisIndex: 0 } : null))
            .filter(Boolean) as any[],
        },
      },
    ],
  };
}

/* ---------------------------------- Combo ---------------------------------- */

function buildCombo(config: AnalystChartConfig): EChartsOption {
  const hasTitle = Boolean(config.title);
  const xField = inferXField(config);
  const series = inferSeries(config);
  const palette = resolvePalette(config.colors);
  const showLegend = series.length > 1;
  const hasRightAxis = series.some((serie) => serie.axis === 'right');
  const comboCats = rowsOf(config).map((row) => asString(row[xField]));
  const yAxis = hasRightAxis
    ? [
        valueYAxis(config),
        { type: 'value', nameTextStyle: FONT.axis, axisLabel: { ...FONT.axis, formatter: axisFmt }, splitLine: { show: false } },
      ]
    : [valueYAxis(config)];

  return {
    ...titleBlock(config.title),
    tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
    legend: showLegend ? topLegend(hasTitle) : undefined,
    grid: cartesianGrid(config, comboCats, { hasTitle, hasLegend: showLegend, right: hasRightAxis ? 56 : 24 }),
    xAxis: categoryXAxis(config, comboCats),
    yAxis,
    series: series.map((serie, index) => ({
      name: serie.label ?? serie.field,
      type: serie.type ?? (index === 0 ? 'bar' : 'line'),
      yAxisIndex: hasRightAxis && serie.axis === 'right' ? 1 : 0,
      smooth: serie.type === 'line',
      stack: serie.stack,
      barMaxWidth: 36,
      symbolSize: 6,
      itemStyle: { color: resolveColor(serie.color, palette[index % palette.length]) },
      data: rowsOf(config).map((row) => Number(row[serie.field] ?? 0)),
    })),
  };
}

/* ---------------------------------- Heatmap ---------------------------------- */

function buildHeatmap(config: AnalystChartConfig): EChartsOption {
  const hasTitle = Boolean(config.title);
  const rows = rowsOf(config);
  // Resolve column names: prefer explicit config, fall back to the canonical x/y/value keys.
  const xField = config.xField ?? config.categoryField ?? 'x';
  const yField = config.yField ?? 'y';
  const valueField = config.valueField ?? 'value';
  const xCats = (config.xCategories ?? Array.from(new Set(rows.map((row) => asString(row[xField]))))).map((cat) => asString(cat));
  const yCats = (config.yCategories ?? Array.from(new Set(rows.map((row) => asString(row[yField]))))).map((cat) => asString(cat));
  const data: number[][] = rows.map((row) => [
    xCats.indexOf(asString(row[xField])),
    yCats.indexOf(asString(row[yField])),
    Number(row[valueField] ?? 0),
  ]);
  const maxValue = Math.max(1, ...data.map((cell) => cell[2]));
  const palette = resolvePalette(config.colors);
  const xRotate = xCats.length > 8 ? 30 : 0;
  return {
    ...titleBlock(config.title),
    tooltip: {
      position: 'top',
      formatter: (p: any) => `${xCats[p.value[0]]} · ${yCats[p.value[1]]}: ${valueFmt(p.value[2])}`,
    },
    // Bottom reserves room for x ticks + x-axis title + the (taller) colour scale.
    grid: { ...baseGrid(hasTitle, false, Boolean(config.yLabel)), bottom: 96 },
    xAxis: {
      type: 'category',
      data: xCats,
      name: config.xLabel,
      nameLocation: 'middle',
      nameGap: xRotate ? 50 : 28,
      nameTextStyle: { ...FONT.axis, fontWeight: 600 },
      splitArea: { show: true },
      axisLabel: { ...FONT.axis, interval: 0, rotate: xRotate },
    },
    yAxis: {
      type: 'category',
      data: yCats,
      name: config.yLabel,
      nameTextStyle: Y_NAME_STYLE,
      splitArea: { show: true },
      axisLabel: { ...FONT.axis, interval: 0 },
    },
    visualMap: {
      min: 0,
      max: maxValue,
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: 10,
      // A long, thick bar is far easier to read and drag than the old 14×10 chip.
      itemWidth: 18,
      itemHeight: 220,
      precision: 0,
      formatter: (value: number) => axisFmt(Math.round(value)),
      text: ['High', 'Low'],
      textGap: 8,
      inRange: { color: ['#EBF8FF', palette[0]] },
      textStyle: FONT.axis,
    },
    series: [
      {
        type: 'heatmap',
        data,
        label: { show: true, ...FONT.axis, formatter: (p: any) => axisFmt(p.value[2]) },
        emphasis: { itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0,0,0,0.3)' } },
      },
    ],
  };
}

/* ---------------------------------- Scatter ---------------------------------- */

function buildScatter(config: AnalystChartConfig): EChartsOption {
  const hasTitle = Boolean(config.title);
  const rows = rowsOf(config);
  const palette = resolvePalette(config.colors);
  const xField = config.xField ?? 'x';
  const yField = config.yField ?? 'y';
  const sizeField = config.valueField ?? 'size';
  const hasSize = rows.some((row) => typeof (row[sizeField] ?? row['value']) === 'number');
  const points = rows.map((row) => {
    const point: number[] = [Number(row[xField] ?? 0), Number(row[yField] ?? 0)];
    const size = row[sizeField] ?? row['value'];
    if (typeof size === 'number') point.push(size);
    const rawColor = row['color'];
    const rowColor = typeof rawColor === 'string' ? resolveColor(rawColor) : undefined;
    return {
      value: point,
      name: asString(row['name']),
      itemStyle: rowColor ? { color: rowColor } : undefined,
    };
  });
  const maxSize = Math.max(1, ...points.map((p) => p.value[2] ?? 0));
  return {
    ...titleBlock(config.title),
    tooltip: {
      trigger: 'item',
      formatter: (p: any) => {
        const coords = `(${p.value[0]}, ${p.value[1]})`;
        return p.data?.name ? `${p.data.name}: ${coords}` : coords;
      },
    },
    grid: { ...baseGrid(hasTitle, false, Boolean(config.yLabel)), bottom: config.xLabel ? 56 : 44 },
    xAxis: { type: 'value', name: config.xLabel, nameLocation: 'middle', nameGap: 30, nameTextStyle: { ...FONT.axis, fontWeight: 600 }, axisLabel: { ...FONT.axis, formatter: axisFmt }, splitLine: { lineStyle: { color: GRID_COLOR } } },
    yAxis: valueYAxis(config),
    series: [
      {
        type: 'scatter',
        symbolSize: hasSize ? (val: any[]) => 8 + (Number(val[2] ?? 0) / maxSize) * 34 : 12,
        itemStyle: { color: palette[0], opacity: 0.8 },
        data: points,
      },
    ],
  };
}

/* ---------------------------------- Hierarchy ---------------------------------- */

interface RawNode {
  name?: unknown;
  value?: unknown;
  color?: unknown;
  children?: unknown[];
}

function mapTreemapNode(node: RawNode): EChartsOption {
  const children = Array.isArray(node.children) ? node.children.map((child) => mapTreemapNode(child as RawNode)) : undefined;
  return {
    name: asString(node.name),
    value: typeof node.value === 'number' ? node.value : undefined,
    itemStyle: typeof node.color === 'string' ? { color: resolveColor(node.color) } : undefined,
    children,
  };
}

/** Sum the value of every leaf reachable from `node` so the root tooltip shows a real total. */
function totalNodeValue(node: RawNode): number {
  if (Array.isArray(node.children) && node.children.length > 0) {
    return node.children.reduce((sum: number, child) => sum + totalNodeValue(child as RawNode), 0);
  }
  return typeof node.value === 'number' ? node.value : 0;
}

function buildTreemap(config: AnalystChartConfig): EChartsOption {
  const rows = rowsOf(config) as unknown as RawNode[];
  const palette = resolvePalette(config.colors);
  const grandTotal = rows.reduce((sum, row) => sum + totalNodeValue(row), 0) || 1;

  return {
    ...titleBlock(config.title),
    tooltip: {
      trigger: 'item',
      // ECharts treemap pre-aggregates `p.value` for parents → tooltip works at every depth.
      formatter: (p: any) => {
        const pct = grandTotal ? ((Number(p.value) / grandTotal) * 100).toFixed(1) : '0';
        const path = Array.isArray(p.treePathInfo) && p.treePathInfo.length > 1
          ? p.treePathInfo.slice(1).map((node: any) => node.name).join(' › ')
          : p.name;
        return `<div style="font-family:Inter,sans-serif">
          <div style="font-weight:600;color:#003A79;margin-bottom:4px">${path}</div>
          <div>${valueFmt(p.value)} <span style="color:#94A3B8">(${pct}%)</span></div>
        </div>`;
      },
    },
    series: [
      {
        type: 'treemap',
        roam: false,
        // Click a tile to zoom into that branch; users return via the breadcrumb.
        nodeClick: 'zoomToNode',
        breadcrumb: {
          show: true,
          left: 'center',
          bottom: 6,
          height: 22,
          itemStyle: {
            color: '#F1F5F9',
            borderColor: '#E2E8F0',
            borderWidth: 1,
            shadowBlur: 0,
            textStyle: { ...FONT.axis, color: TITLE_COLOR, fontWeight: 600 },
          },
          emphasis: { itemStyle: { color: '#E0F2FE', textStyle: { color: TITLE_COLOR } } },
        },
        top: config.title ? 44 : 12,
        bottom: 36,
        left: 8,
        right: 8,
        // Leaves get the smallest minimum so dense trees still surface label text.
        leafDepth: undefined,
        upperLabel: {
          show: true,
          height: 22,
          ...FONT.axis,
          color: '#fff',
          fontWeight: 600,
          fontSize: 12,
          formatter: (p: any) => `${p.name}  (${valueFmt(p.value)})`,
        },
        label: {
          show: true,
          position: 'insideTopLeft',
          ...FONT.axis,
          color: '#fff',
          fontSize: 12,
          fontWeight: 600,
          // Hide labels on tiles too small to fit them — keeps the visual clean.
          formatter: (p: any) => {
            const w = p?.layout?.width ?? 0;
            const h = p?.layout?.height ?? 0;
            if (w < 60 || h < 24) return '';
            if (w < 120) return p.name;
            return `${p.name}\n${valueFmt(p.value)}`;
          },
          overflow: 'truncate',
        },
        itemStyle: { borderColor: '#fff', borderWidth: 1, gapWidth: 2 },
        // Distinct hue per top-level branch (tenant) + softer saturation as we descend.
        levels: [
          {
            // Root container — invisible; lets level-1 tiles pick the palette.
            itemStyle: { borderColor: 'transparent', borderWidth: 0, gapWidth: 4 },
          },
          {
            // Top-level (e.g. tenant). Each gets its own color from the palette.
            color: palette,
            colorMappingBy: 'index',
            itemStyle: { borderColor: '#fff', borderWidth: 2, gapWidth: 2 },
            upperLabel: { show: true, height: 22, ...FONT.axis, color: '#fff', fontWeight: 600 },
          },
          {
            // Mid-level (e.g. advisor). Inherits parent hue, softer saturation.
            colorSaturation: [0.35, 0.6],
            itemStyle: { borderColorSaturation: 0.7, gapWidth: 1, borderWidth: 1 },
          },
          {
            // Leaves (e.g. response category). Lightest tint.
            colorSaturation: [0.25, 0.45],
            itemStyle: { gapWidth: 1, borderWidth: 1 },
          },
        ],
        data: rows.map((row) => mapTreemapNode(row)),
      },
    ],
  };
}

/** Convert a `#rrggbb` hex to an `rgba()` string so we can tint shadows / links. */
function hexToRgba(hex: string, alpha: number): string {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!match) return hex;
  const r = Number.parseInt(match[1], 16);
  const g = Number.parseInt(match[2], 16);
  const b = Number.parseInt(match[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Build an ECharts `image://` symbol that renders a circular-progress ring:
 * a light track behind a coloured arc filled to `percent` (0–1). The exact
 * percentage is surfaced on hover (tooltip), so the ring stays label-free.
 */
function circularProgressSymbol(percent: number, color: string): string {
  const clamped = Math.max(0, Math.min(1, Number.isFinite(percent) ? percent : 0));
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - clamped);
  // Translucent track + solid progress arc + solid inner disc, all in the primary hue.
  const track = hexToRgba(color, 0.22);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">` +
    `<circle cx="24" cy="24" r="${radius}" fill="none" stroke="${track}" stroke-width="5"/>` +
    `<circle cx="24" cy="24" r="${radius}" fill="none" stroke="${color}" stroke-width="5" stroke-linecap="round" ` +
    `stroke-dasharray="${circumference.toFixed(2)}" stroke-dashoffset="${offset.toFixed(2)}" transform="rotate(-90 24 24)"/>` +
    `<circle cx="24" cy="24" r="17" fill="${color}"/>` +
    `</svg>`;
  return `image://data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/** Smallest node size allowed — kept large enough to stay clearly viewable. */
const TREE_MIN_SYMBOL = 28;

/** Nodes shrink gently with depth (root biggest) but never below the min. */
function symbolSizeForDepth(depth: number): number {
  return Math.max(TREE_MIN_SYMBOL, 52 - depth * 6);
}

/**
 * Map a raw hierarchy node to an ECharts tree node: a circular-progress ring
 * (value vs root total), a stable path `id` for controlled expand/collapse, and
 * per-depth size reduction (`depth` 1 = first level).
 */
function mapTreeNode(node: RawNode, branchColor: string, rootTotal: number, id: string, depth: number): EChartsOption {
  const value = typeof node.value === 'number' ? node.value : undefined;
  const children = Array.isArray(node.children)
    ? node.children.map((child, index) => mapTreeNode(child as RawNode, branchColor, rootTotal, `${id}/${index}`, depth + 1))
    : undefined;
  const hasChildren = !!children && children.length > 0;
  const percent = value !== undefined && rootTotal > 0 ? value / rootTotal : 0;
  return {
    id,
    name: asString(node.name),
    value,
    symbol: circularProgressSymbol(percent, branchColor),
    symbolSize: symbolSizeForDepth(depth),
    label: {
      formatter:
        value === undefined
          ? `{name|${asString(node.name)}}`
          : `{name|${asString(node.name)}}\n{val|${valueFmt(value)}}`,
    },
    lineStyle: { color: branchColor },
    ...(hasChildren ? { collapsed: false } : {}),
    children,
  };
}

function buildTree(config: AnalystChartConfig): EChartsOption {
  const rows = rowsOf(config) as unknown as RawNode[];
  const primaryColor = CHART_TOKENS['chart-primary'];

  // ECharts tree needs one root: use the single row, else wrap rows in a synthetic root.
  const singleRoot = rows.length === 1;
  const rootNode = singleRoot ? rows[0] : undefined;
  const branches = (singleRoot ? (rootNode?.children ?? []) : rows) as RawNode[];

  const grandTotal = branches.reduce((sum, branch) => sum + totalNodeValue(branch), 0);
  const rootName = singleRoot ? asString(rootNode?.name) : 'All';
  let rootValue = grandTotal;
  if (singleRoot) {
    rootValue = typeof rootNode?.value === 'number' ? rootNode.value : totalNodeValue(rootNode as RawNode);
  }
  const rootTotal = rootValue || grandTotal || 1;

  const mappedBranches = branches.map((branch, index) =>
    mapTreeNode(branch, primaryColor, rootTotal, `r/${index}`, 1)
  );

  const treeData = [
    {
      id: 'r',
      name: rootName,
      value: rootValue,
      symbol: circularProgressSymbol(1, primaryColor),
      symbolSize: 58,
      // Name + count stacked on separate lines, same as the other nodes.
      label: {
        formatter:
          typeof rootValue === 'number'
            ? `{root|${rootName}}\n{rootVal|${valueFmt(rootValue)}}`
            : `{root|${rootName}}`,
      },
      collapsed: false,
      children: mappedBranches,
    },
  ];

  return {
    ...titleBlock(config.title),
    tooltip: {
      trigger: 'item',
      triggerOn: 'mousemove',
      confine: true,
      backgroundColor: 'rgba(255,255,255,0.98)',
      borderColor: CHART_TOKENS['chart-grid'],
      borderWidth: 1,
      padding: [8, 12],
      extraCssText: 'border-radius:10px;box-shadow:0 6px 24px rgba(0,58,121,0.16);max-width:260px;white-space:normal;',
      formatter: (p: any) => {
        const value = Number(p.value);
        const hasValue = typeof p.value === 'number' && Number.isFinite(value);
        const pctOfTotal = grandTotal && hasValue ? ((value / grandTotal) * 100).toFixed(1) : null;
        const path = Array.isArray(p.treeAncestors) && p.treeAncestors.length > 1
          ? p.treeAncestors.map((node: any) => node.name).filter(Boolean).join(' › ')
          : p.name;
        const pctLine = pctOfTotal !== null
          ? `<div style="margin-top:4px;font-weight:600;color:var(--cty-text-primary,#003A79);font-size:13px">${pctOfTotal}% of total</div>`
          : '';
        return `<div style="font-family:Inter,sans-serif;max-width:236px;white-space:normal;word-break:break-word;line-height:1.45">
          <div style="font-size:11px;color:var(--cty-text-secondary,#94A3B8)">${path}</div>
          ${pctLine}
        </div>`;
      },
    },
    series: [
      {
        type: 'tree',
        data: treeData,
        // Generous margins so labels at the extremes are not clipped.
        top: config.title ? '16%' : '8%',
        bottom: '8%',
        left: '10%',
        right: '18%',
        layout: 'orthogonal',
        orient: 'LR',
        // Zoom + drag enabled, bounded to 0.5×–3×.
        roam: true,
        scaleLimit: { min: 0.5, max: 3 },
        edgeShape: 'curve',
        edgeForkPosition: '58%',
        label: {
          position: 'top',
          distance: 8,
          align: 'center',
          verticalAlign: 'bottom',
          // content1 surface; dark values are injected by AnalystChart.
          color: CONTENT1_FG,
          backgroundColor: CONTENT1_BG,
          borderColor: CONTENT1_BORDER,
          borderWidth: 1,
          padding: [4, 7],
          borderRadius: 7,
          shadowBlur: 4,
          shadowColor: 'rgba(0,58,121,0.10)',
          rich: {
            name: { fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 600, lineHeight: 15 },
            val: { fontFamily: 'Inter, sans-serif', fontSize: 10, lineHeight: 13 },
            root: { fontFamily: 'Oswald, sans-serif', fontSize: 12, fontWeight: 700, lineHeight: 17 },
            rootVal: { fontFamily: 'Inter, sans-serif', fontSize: 10, lineHeight: 13 },
          },
        },
        leaves: {
          label: {
            position: 'right',
            distance: 10,
            align: 'left',
            verticalAlign: 'middle',
          },
        },
        lineStyle: { color: primaryColor, width: 1.5, curveness: 0.25 },
        emphasis: {
          // Hover highlights the ancestor path up to the root.
          focus: 'ancestor',
          lineStyle: { width: 2.5 },
          label: { shadowBlur: 8, shadowColor: 'rgba(0,58,121,0.18)' },
        },
        // Collapse is controlled via node `collapsed` flags by AnalystChart.
        expandAndCollapse: false,
        animationDuration: 450,
        animationDurationUpdate: 500,
        animationEasing: 'cubicOut',
      },
    ],
  };
}

/**
 * Returns an ECharts option for the supplied analyst chart config, or `null`
 * when the chartType is unknown.
 */
export function buildChartOption(config: AnalystChartConfig): EChartsOption | null {
  switch (config.chartType) {
    case 'bar':
    case 'line':
    case 'step':
    case 'multi_bar':
    case 'stacked_bar':
    case 'multi_line':
      return buildCartesian(config);
    case 'hbar':
      return buildHbar(config);
    case 'combo':
      return buildCombo(config);
    case 'pareto':
      return buildPareto(config);
    case 'waterfall':
      return buildWaterfall(config);
    case 'bullet':
      return buildBullet(config);
    case 'donut':
      return buildDonut(config);
    case 'funnel':
      return buildFunnel(config);
    case 'gauge':
      return buildGauge(config);
    case 'treemap':
      return buildTreemap(config);
    case 'decomp_tree':
      return buildTree(config);
    case 'heatmap':
      return buildHeatmap(config);
    case 'scatter':
      return buildScatter(config);
    default:
      return null;
  }
}
